import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Trash2, Copy, Eye } from 'lucide-react';

export interface Scene {
  sceneNumber: number;
  title: string;
  visualDescription: string;
  characters: string[];
  setting: string;
  mood: string;
  timestamp: string;
  scriptContent: string;
  suggestedAnimation: 'zoom-in' | 'pan-left' | 'pan-right' | 'zoom-out' | 'static';
  imageUrl?: string;
  imagePrompt?: string;
}

interface SceneEditorProps {
  scenes: Scene[];
  onScenesChange: (scenes: Scene[]) => void;
  onSave: () => void;
  isSaving?: boolean;
}

export const SceneEditor: React.FC<SceneEditorProps> = ({
  scenes,
  onScenesChange,
  onSave,
  isSaving = false
}) => {
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(0);
  const [editingScene, setEditingScene] = useState<Scene | null>(scenes[0] || null);
  const [showPreview, setShowPreview] = useState(true);

  const currentScene = editingScene || scenes[selectedSceneIndex];

  const updateScene = (updates: Partial<Scene>) => {
    const updatedScene = { ...currentScene, ...updates };
    setEditingScene(updatedScene);

    const newScenes = [...scenes];
    newScenes[selectedSceneIndex] = updatedScene;
    onScenesChange(newScenes);
  };

  const moveScene = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= scenes.length) return;

    const newScenes = [...scenes];
    const [movedScene] = newScenes.splice(fromIndex, 1);
    newScenes.splice(toIndex, 0, movedScene);

    // Recalculate scene numbers
    newScenes.forEach((scene, idx) => {
      scene.sceneNumber = idx + 1;
    });

    onScenesChange(newScenes);

    // Update selected index if needed
    if (selectedSceneIndex === fromIndex) {
      setSelectedSceneIndex(toIndex);
    }

    setEditingScene(null);
  };

  const deleteScene = (index: number) => {
    if (scenes.length <= 1) {
      alert('Cannot delete the last scene');
      return;
    }

    const newScenes = scenes.filter((_, i) => i !== index);
    newScenes.forEach((scene, idx) => {
      scene.sceneNumber = idx + 1;
    });

    onScenesChange(newScenes);
    setSelectedSceneIndex(Math.min(index, newScenes.length - 1));
    setEditingScene(null);
  };

  const duplicateScene = (index: number) => {
    const sceneToDuplicate = scenes[index];
    const newScene: Scene = {
      ...sceneToDuplicate,
      sceneNumber: scenes.length + 1
    };

    const newScenes = [...scenes, newScene];
    onScenesChange(newScenes);
    setSelectedSceneIndex(newScenes.length - 1);
    setEditingScene(newScene);
  };

  const parseTimestamp = (timestamp: string): number => {
    // Parse "[0:00-0:30]" format and return duration in seconds
    const match = timestamp.match(/\[(\d+):(\d+)-(\d+):(\d+)\]/);
    if (!match) return 0;

    const [, startMin, startSec, endMin, endSec] = match.map(Number);
    const start = startMin * 60 + startSec;
    const end = endMin * 60 + endSec;
    return end - start;
  };

  const totalDuration = scenes.reduce((sum, scene) => sum + parseTimestamp(scene.timestamp), 0);

  const animationOptions: Scene['suggestedAnimation'][] = [
    'zoom-in',
    'pan-left',
    'pan-right',
    'zoom-out',
    'static'
  ];

  if (!currentScene) {
    return <div className="p-6 text-center text-gray-500">No scenes available</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
      {/* Scene List Sidebar */}
      <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
          <h2 className="text-lg font-semibold">Scenes</h2>
          <p className="text-sm opacity-90">{scenes.length} scenes • {Math.floor(totalDuration / 60)}:{String(totalDuration % 60).padStart(2, '0')}</p>
        </div>

        <div className="overflow-y-auto max-h-[600px]">
          {scenes.map((scene, index) => (
            <div
              key={index}
              className={`border-b border-gray-100 p-3 cursor-pointer transition-colors ${
                selectedSceneIndex === index
                  ? 'bg-indigo-50 border-l-4 border-l-indigo-600'
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => {
                setSelectedSceneIndex(index);
                setEditingScene(null);
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900">Scene {scene.sceneNumber}</div>
                  <div className="text-xs text-gray-600 truncate">{scene.title}</div>
                  <div className="text-xs text-gray-500">{scene.timestamp}</div>
                </div>
                {scene.imageUrl && (
                  <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                    <img src={scene.imageUrl} alt="" className="w-full h-full object-cover rounded" />
                  </div>
                )}
              </div>

              {/* Mini Controls */}
              <div className="flex gap-1 mt-2 opacity-0 hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveScene(index, index - 1);
                  }}
                  disabled={index === 0}
                  className="p-1 hover:bg-indigo-200 rounded disabled:opacity-30"
                  title="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveScene(index, index + 1);
                  }}
                  disabled={index === scenes.length - 1}
                  className="p-1 hover:bg-indigo-200 rounded disabled:opacity-30"
                  title="Move down"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    duplicateScene(index);
                  }}
                  className="p-1 hover:bg-green-200 rounded"
                  title="Duplicate"
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteScene(index);
                  }}
                  className="p-1 hover:bg-red-200 rounded"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editor & Preview */}
      <div className="lg:col-span-2 space-y-6">
        {/* Toggle Preview */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit Scene {currentScene.sceneNumber}</h3>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            <Eye size={16} />
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Edit Form */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={currentScene.title}
                onChange={(e) => updateScene({ title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Timestamp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timestamp</label>
              <input
                type="text"
                value={currentScene.timestamp}
                onChange={(e) => updateScene({ timestamp: e.target.value })}
                placeholder="[0:00-0:30]"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Duration: {parseTimestamp(currentScene.timestamp)}s</p>
            </div>

            {/* Mood */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mood</label>
              <input
                type="text"
                value={currentScene.mood}
                onChange={(e) => updateScene({ mood: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Animation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Animation</label>
              <select
                value={currentScene.suggestedAnimation}
                onChange={(e) => updateScene({ suggestedAnimation: e.target.value as Scene['suggestedAnimation'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {animationOptions.map((anim) => (
                  <option key={anim} value={anim}>
                    {anim.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            {/* Setting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Setting</label>
              <input
                type="text"
                value={currentScene.setting}
                onChange={(e) => updateScene({ setting: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Characters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Characters (comma-separated)</label>
              <input
                type="text"
                value={currentScene.characters.join(', ')}
                onChange={(e) => updateScene({ characters: e.target.value.split(',').map(c => c.trim()) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-4 space-y-4">
              {/* Image Preview */}
              {currentScene.imageUrl && (
                <div className="rounded-lg overflow-hidden h-40 bg-gray-300">
                  <img src={currentScene.imageUrl} alt={currentScene.title} className="w-full h-full object-cover" />
                </div>
              )}

              {/* Scene Summary */}
              <div className="bg-white rounded-lg p-4 space-y-3">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Title</div>
                  <div className="text-sm font-semibold text-gray-900">{currentScene.title}</div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Visual Description</div>
                  <div className="text-xs text-gray-700 line-clamp-3">{currentScene.visualDescription}</div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase">Animation</div>
                    <div className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded inline-block">
                      {currentScene.suggestedAnimation}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase">Duration</div>
                    <div className="text-xs text-gray-700">{parseTimestamp(currentScene.timestamp)}s</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Script</div>
                  <div className="text-xs text-gray-700 italic">"{currentScene.scriptContent}"</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Visual & Script Content */}
        <div className="space-y-4 bg-white rounded-lg border border-gray-200 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Visual Description</label>
            <textarea
              value={currentScene.visualDescription}
              onChange={(e) => updateScene({ visualDescription: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Script Content</label>
            <textarea
              value={currentScene.scriptContent}
              onChange={(e) => updateScene({ scriptContent: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
